import {
  Component,
  Output,
  EventEmitter,
  AfterViewInit,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectorRef,
  Renderer2,
  ElementRef,
} from '@angular/core';
import { NavigationEnd, Router, Event } from '@angular/router';
import { Subscription } from 'rxjs';
import { SharedServices } from '../../shared.service';
import { MenuItem } from 'src/app/interfaces/userLogin.interface';
import { ConnectedPosition, ScrollStrategyOptions } from '@angular/cdk/overlay';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  overlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
    },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' }, // fallback izquierda
  ];
  activeSubmenuItem: any = null;
  @Output() toggle = new EventEmitter<boolean>();
  isExpanded = false;
  userRole: string = '';
  accessList: string[] = [];
  private readonly roleAccessMap: Record<string, string[]> = {
    Distribuidor: [
      'Dashboard',
      'Productos',
      'Autorizaciones',
      'Pacientes',
      'VITALES_DISTRIBUIDOR',
      'RIPS',
      'PQR_ADMIN',
      'Mipres',
      'ONBOARDING_DISTRIBUIDOR',
      'WHATSAPP',
      'Integrations',
      'logistica',
      'eventos_Adversos',
      'informes',
    ],
    EPS: [
      'Dashboard_EPS',
      'Autorizaciones_EPS',
      'Pacientes_EPS',
      'VITALES_EPS',
      'informes',
    ],
    Paciente: [
      'Mis_Datos',
      'Autorizaciones_Pacientes',
      'PQR',
      'Preguntas_Frecuentes',
    ],
    PQRs: ['PQR_ADMIN'],
    LAB: ['Dashboard_LAB', 'Autorizaciones_LAB', 'VITALES_LAB', 'informes'],
  };
  navItems: any[] = [];
  ruta: string = '';
  name: string = '';
  showRolEps: boolean = false;

  private submenuEl: HTMLElement | null = null;
  private submenuMouseLeaveTimeout: any = null;
  private docClickUnlisten: (() => void) | null = null;
  private scrollResizeUnlisten: Array<() => void> = [];

  constructor(
    private router: Router,
    private sharedService: SharedServices,
    private sso: ScrollStrategyOptions,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    this.navItems = this.sharedService.userLogin.menu;
    this.name = this.sharedService.userLogin.name;
  }

  get scrollStrategy() {
    return this.sso.reposition(); // o this.sso.close() si prefieres cerrar
  }

  ngOnInit(): void {
    this.userRole = this.sharedService.userLogin.rol;
    this.showRolEps = this.userRole != 'EPS';
    this.accessList = this.roleAccessMap[this.userRole] || [];

    this.filterNavItems();
  }
  ngAfterViewInit(): void {
    // Listener global para cerrar submenu al hacer click fuera
    this.docClickUnlisten = this.renderer.listen(
      'document',
      'click',
      (ev: MouseEvent) => {
        if (!this.submenuEl) return;
        const target = ev.target as Node;
        if (!this.submenuEl.contains(target)) {
          this.closeSubmenuImmediate();
        }
      },
    );

    // cerrar en scroll/resize
    this.scrollResizeUnlisten.push(
      this.renderer.listen('window', 'scroll', () =>
        this.closeSubmenuImmediate(),
      ),
      this.renderer.listen('window', 'resize', () =>
        this.closeSubmenuImmediate(),
      ),
    );
  }

  ngOnDestroy(): void {
    this.closeSubmenuImmediate();
    if (this.docClickUnlisten) this.docClickUnlisten();
    this.scrollResizeUnlisten.forEach((un) => un());
  }

  openSubmenu(event: MouseEvent, item: any) {
    // cancelar cierre pendiente
    if (this.submenuMouseLeaveTimeout) {
      clearTimeout(this.submenuMouseLeaveTimeout);
      this.submenuMouseLeaveTimeout = null;
    }

    // si no hay subMenu -> cerrar cualquier submenu abierto
    if (!item?.subMenu || !item.subMenu.length) {
      this.closeSubmenuImmediate();
      return;
    }

    // si ya hay un submenu abierto, eliminarlo para recrearlo
    this.closeSubmenuImmediate();

    // crear contenedor del submenu
    const container = this.renderer.createElement('div');
    this.submenuEl = container;
    this.renderer.addClass(container, 'floating-submenu');

    // estilos base (puedes mover a CSS global si prefieres)
    this.renderer.setStyle(container, 'position', 'fixed');
    this.renderer.setStyle(container, 'zIndex', '99999');
    this.renderer.setStyle(container, 'padding', '8px');
    this.renderer.setStyle(container, 'borderRadius', '8px');
    this.renderer.setStyle(
      container,
      'boxShadow',
      '0 8px 24px rgba(0,0,0,0.4)',
    );
    this.renderer.setStyle(container, 'background', 'var(--color_box,#23476b)');
    this.renderer.setStyle(container, 'color', 'var(--text-white, #fff)');
    this.renderer.setStyle(container, 'maxWidth', '320px');
    this.renderer.setStyle(container, 'minWidth', '160px');
    this.renderer.setStyle(container, 'overflow', 'auto');
    this.renderer.setStyle(container, 'maxHeight', '70vh');
    this.renderer.setStyle(container, 'pointerEvents', 'auto');

    // título (si sidebar expandido)
    if (this.isExpanded) {
      const title = this.renderer.createElement('p');
      this.renderer.addClass(title, 'submenu-title');
      this.renderer.setStyle(title, 'fontWeight', '600');
      this.renderer.setStyle(title, 'margin', '0 0 8px 0');
      this.renderer.appendChild(
        title,
        this.renderer.createText(item.label || ''),
      );
      this.renderer.appendChild(container, title);
    }

    // lista de subitems
    const ul = this.renderer.createElement('ul');
    this.renderer.setStyle(ul, 'listStyle', 'none');
    this.renderer.setStyle(ul, 'padding', '0');
    this.renderer.setStyle(ul, 'margin', '0');

    item.subMenu.forEach((sub: any) => {
      const li = this.renderer.createElement('li');
      this.renderer.addClass(li, 'floating-submenu-item');
      this.renderer.setStyle(li, 'padding', '8px 12px');
      this.renderer.setStyle(li, 'cursor', 'pointer');
      this.renderer.setStyle(li, 'whiteSpace', 'normal');
      this.renderer.appendChild(li, this.renderer.createText(sub.label || ''));

      // click en subitem -> navegar y cerrar
      this.renderer.listen(li, 'click', (ev: MouseEvent) => {
        ev.stopPropagation();
        this.navigateTo(sub);
        this.closeSubmenuImmediate();
      });

      // evitar cierre si el mouse entra al submenu
      this.renderer.listen(li, 'mouseenter', () => {
        if (this.submenuMouseLeaveTimeout) {
          clearTimeout(this.submenuMouseLeaveTimeout);
          this.submenuMouseLeaveTimeout = null;
        }
      });

      this.renderer.listen(li, 'mouseleave', () => {
        this.startCloseSubmenuTimer();
      });

      this.renderer.appendChild(ul, li);
    });

    this.renderer.appendChild(container, ul);

    // listeners para mantener abierto si se mueve el mouse al overlay
    this.renderer.listen(container, 'mouseenter', () => {
      if (this.submenuMouseLeaveTimeout) {
        clearTimeout(this.submenuMouseLeaveTimeout);
        this.submenuMouseLeaveTimeout = null;
        this.toggleSidebar(true);
      }
    });
    this.renderer.listen(container, 'mouseleave', () => {
      this.startCloseSubmenuTimer();
      this.toggleSidebar(false);
    });

    // anexa al body
    this.renderer.appendChild(document.body, container);

    // posicionar respecto al li
    const liEl =
      (event.currentTarget as HTMLElement) ?? (event.target as HTMLElement);
    const rect = liEl.getBoundingClientRect();

    // posición tentativa (derecha del li)
    let left = rect.right + 8;
    let top = rect.top;

    // posicionamos temporalmente y después ajustamos
    this.renderer.setStyle(container, 'left', `${left}px`);
    this.renderer.setStyle(container, 'top', `${top}px`);

    // medir y ajustar en next frame
    requestAnimationFrame(() => {
      if (!this.submenuEl) return;
      const subRect = this.submenuEl.getBoundingClientRect();

      // si no cabe a la derecha, posicionar a la izquierda
      if (left + subRect.width > window.innerWidth - 8) {
        left = rect.left - subRect.width - 8;
      }
      // ajustar vertical si desborda abajo
      if (top + subRect.height > window.innerHeight - 8) {
        top = Math.max(8, window.innerHeight - subRect.height - 8);
      }
      if (top < 8) top = 8;

      this.renderer.setStyle(this.submenuEl!, 'left', `${Math.round(left)}px`);
      this.renderer.setStyle(this.submenuEl!, 'top', `${Math.round(top)}px`);
    });
  }

  /** Inicia timer para cerrar el submenu (pequeño delay para evitar parpadeos) */
  startCloseSubmenuTimer() {
    if (this.submenuMouseLeaveTimeout)
      clearTimeout(this.submenuMouseLeaveTimeout);
    this.submenuMouseLeaveTimeout = setTimeout(() => {
      this.closeSubmenuImmediate();
      this.submenuMouseLeaveTimeout = null;
    }, 180);
  }

  /** Cierra y elimina inmediatamente el submenu */
  closeSubmenuImmediate() {
    if (this.submenuMouseLeaveTimeout) {
      clearTimeout(this.submenuMouseLeaveTimeout);
      this.submenuMouseLeaveTimeout = null;
    }
    if (this.submenuEl) {
      try {
        this.renderer.removeChild(document.body, this.submenuEl);
      } catch (err) {}
      this.submenuEl = null;
    }
  }

  toggleSubmenu(item: any, event: MouseEvent, scrollContainer: HTMLElement) {
    if (this.activeSubmenuItem === item) {
      this.activeSubmenuItem = null;
    } else {
      this.activeSubmenuItem = item;

      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Posición relativa dentro del contenedor con scroll
      const relativeTop =
        rect.top - containerRect.top + scrollContainer.scrollTop;

      item.positionTop = relativeTop;
    }
  }

  get sidebarWidth(): number {
    return this.isExpanded ? window.innerWidth * 0.1 : window.innerWidth * 0.05;
  }

  filterNavItems() {
    this.navItems = this.navItems.filter((item) =>
      this.accessList.includes(item.moduleKey),
    );
  }

  toggleSidebar(estado: boolean) {
    this.isExpanded = estado;
    this.toggle.emit(this.isExpanded);
  }

  onMouseEnter(item: any) {
    item.showSubmenu = true;
  }

  onMouseLeave(item: any) {
    item.showSubmenu = false;
  }

  navigateTo(item: any) {
    if (item.url != '') {
      this.router.navigateByUrl(item.url);
      this.activeSubmenuItem = null; // cerrar el submenu al navegar
    }
  }

  validateAccessList(module: string): boolean {
    if (this.accessList?.includes(module)) return true;
    return false;
  }

  exit() {
    localStorage.clear();
    this.router.navigateByUrl('login');
  }
}
